import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { AlertTriangle, Camera, CameraOff, CheckCircle2, LogIn, QrCode, XCircle } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { NoFacilitiesNotice, PageHeader, ScopeFilters } from "../../components/visitors/VisitorShared";
import { CheckInDialog, IncidentDialog } from "../../components/visitors/VisitorForms";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { parseVisitLabel } from "../../components/visitors/VisitLabel";
import { fmtDateTime, visitorApi } from "../../components/visitors/visitorUtils";
import { useApiMessages } from "../../hooks/useApiMessages";

const REGION_ID = "visitor-qr-reader";

export default function QrScanner() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [manual, setManual] = useState("");
  const [token, setToken] = useState(null);
  const [result, setResult] = useState(null);
  const [checkInOpen, setCheckInOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [visitId, setVisitId] = useState(null);

  const stopCamera = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setScanning(false);
    if (scanner) {
      try {
        await scanner.stop();
        scanner.clear();
      } catch {
        // El escáner ya estaba detenido
      }
    }
  };

  useEffect(() => () => {
    stopCamera();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = async (raw) => {
    if (busyRef.current || !raw?.trim()) return;
    // QR de una etiqueta de entrada: muestra la visita en lugar de validar un pase
    const labelVisitId = parseVisitLabel(raw);
    if (labelVisitId) {
      setResult(null);
      setVisitId(labelVisitId);
      return;
    }
    busyRef.current = true;
    try {
      const res = await visitorApi.post("/qr/validate", {
        token: raw.trim(),
        facility_id: scope.facilityId !== "all" ? scope.facilityId : null,
      });
      setToken(raw.trim());
      setResult(res);
      if (res.valid) toast.success(t("visitors.scan.valid"));
      else toast.error(t(`visitors.qrReasons.${res.reason}`));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    } finally {
      busyRef.current = false;
    }
  };

  const startCamera = async () => {
    setResult(null);
    setScanning(true);
    try {
      const scanner = new Html5Qrcode(REGION_ID);
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decoded) => {
          await stopCamera();
          validate(decoded);
        },
        () => {}
      );
    } catch (e) {
      console.error("Error iniciando cámara:", e);
      toast.error(t("visitors.scan.cameraError"));
      await stopCamera();
    }
  };

  const reset = () => {
    setResult(null);
    setToken(null);
    setManual("");
  };

  const pre = result?.preauth;

  return (
    <div className="space-y-6">
      <PageHeader title={t("visitors.scan.title")} subtitle={t("visitors.scan.subtitle")} />
      <ScopeFilters scope={scope} />
      {scope.loaded && scope.facilities.length === 0 && <NoFacilitiesNotice isManager={scope.isManager} />}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              {t("visitors.scan.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div id={REGION_ID} className={`w-full overflow-hidden rounded-lg ${scanning ? "min-h-[260px] bg-black" : ""}`} />
            {scanning ? (
              <Button variant="outline" className="w-full" onClick={stopCamera}>
                <CameraOff className="h-4 w-4 mr-2" />
                {t("visitors.scan.stopCamera")}
              </Button>
            ) : (
              <Button className="w-full" size="lg" onClick={startCamera}>
                <Camera className="h-5 w-5 mr-2" />
                {t("visitors.scan.startCamera")}
              </Button>
            )}
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                validate(manual);
              }}
            >
              <Input placeholder={t("visitors.scan.manualCode")} value={manual} onChange={(e) => setManual(e.target.value)} />
              <Button type="submit" variant="outline">
                {t("visitors.scan.validate")}
              </Button>
            </form>
          </CardContent>
        </Card>

        {result && (
          <Card className={result.valid ? "border-green-300" : "border-red-300"}>
            <CardContent className="p-6 space-y-4">
              <div className={`flex items-center gap-3 ${result.valid ? "text-green-700" : "text-red-700"}`}>
                {result.valid ? <CheckCircle2 className="h-10 w-10" /> : <XCircle className="h-10 w-10" />}
                <div>
                  <p className="text-xl font-bold">{result.valid ? t("visitors.scan.valid") : t("visitors.scan.invalid")}</p>
                  {!result.valid && <p className="text-sm">{t(`visitors.qrReasons.${result.reason}`)}</p>}
                </div>
              </div>

              {pre && (
                <div className="flex gap-4">
                  {result.visitor?.photo_base64 && (
                    <img src={result.visitor.photo_base64} alt="" className="h-24 w-24 rounded-lg object-cover border" />
                  )}
                  <div className="space-y-1 text-sm">
                    <p className="text-lg font-semibold">{pre.visitor_name}</p>
                    <p className="text-slate-600">{[pre.visitor_id_number, pre.visitor_company].filter(Boolean).join(" · ")}</p>
                    <p>
                      {t("visitors.common.facility")}: <strong>{pre.facility_name}</strong>
                    </p>
                    {pre.host_name && (
                      <p>
                        {t("visitors.common.host")}: <strong>{pre.host_name}</strong>
                      </p>
                    )}
                    {pre.purpose && (
                      <p>
                        {t("visitors.common.purpose")}: {pre.purpose}
                      </p>
                    )}
                    <p>
                      {t("visitors.scan.validUntil")}: {fmtDateTime(pre.valid_until)}
                    </p>
                    <p>
                      {t("visitors.scan.uses")}: {pre.uses}/{pre.max_uses}
                    </p>
                    {pre.vehicle?.plate && (
                      <p>
                        {t("visitors.common.vehicle")}: {pre.vehicle.plate}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {result.valid && result.requires_id_verification && (
                <p className="rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">
                  {t("visitors.access.idVerificationRequired")}
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                {result.valid && (
                  <Button size="lg" onClick={() => setCheckInOpen(true)}>
                    <LogIn className="h-5 w-5 mr-2" />
                    {t("visitors.scan.proceedCheckIn")}
                  </Button>
                )}
                {!result.valid && scope.facilities.length > 0 && (
                  <Button variant="outline" onClick={() => setIncidentOpen(true)}>
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    {t("visitors.incidents.newIncident")}
                  </Button>
                )}
                <Button variant="outline" onClick={reset}>
                  {t("visitors.scan.scanAgain")}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {pre && (
        <CheckInDialog
          open={checkInOpen}
          onOpenChange={setCheckInOpen}
          scope={scope}
          preset={{ preauth: pre, qrToken: token }}
          onDone={reset}
        />
      )}
      <VisitDetailDialog visitId={visitId} open={!!visitId} onOpenChange={(o) => !o && setVisitId(null)} scope={scope} />
      <IncidentDialog open={incidentOpen} onOpenChange={setIncidentOpen} scope={scope} defaultType="invalid_qr" onDone={reset} />
    </div>
  );
}
